import { DownloadOutlined } from '@ant-design/icons'
import { Button, Dropdown } from 'antd'
import dayjs from 'dayjs'
import { useGeckoAuth } from 'features/auth'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import { useOntology } from 'features/dynamicOntology'
import { useGeckoNotifications } from 'features/notifications'
import { RolodexContext } from 'features/rolodex'
import { FC, useCallback, useContext, useMemo, useState } from 'react'

import {
  ExplorerTableExportColumn,
  ExplorerTableExportFormat,
  ExplorerTableExportInput,
  runExplorerTableExport,
} from './runExplorerTableExport'

type ExplorerTableExportButtonProps = Pick<
  ExplorerTableExportInput,
  | 'groupByType'
  | 'links'
  | 'childLinks'
  | 'orderedGroupIds'
  | 'groupIdToBaseId'
  | 'expandRecords'
  | 'truncated'
> & {
  totalEntries: number
  isPlanLoading: boolean
  columnDefs: ReadonlyArray<DrilldownTableColumnDef>
  enabledExtraColumns: ReadonlyArray<DrilldownTableExtraColumn>
  /*
   * The panel title used as the basis for the download filename. We slug
   * it lightly so the file plays nicely with file systems that disallow
   * spaces or punctuation.
   */
  panelTitle: string
}

function slugifyTitle(title: string): string {
  return (
    title
      .trim()
      .replace(/[^0-9a-zA-Z]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'explorer_export'
  )
}

export const ExplorerTableExportButton: FC<ExplorerTableExportButtonProps> = ({
  groupByType,
  links,
  childLinks,
  orderedGroupIds,
  groupIdToBaseId,
  expandRecords,
  truncated,
  totalEntries,
  isPlanLoading,
  columnDefs,
  enabledExtraColumns,
  panelTitle,
}) => {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { apiURL } = useContext(RolodexContext)
  const { fields: schemaFields } = useOntology()
  const {
    successNotification,
    errorNotification,
    warningNotification,
    infoNotification,
  } = useGeckoNotifications()
  const [isExporting, setIsExporting] = useState(false)

  const exportColumns = useMemo<ReadonlyArray<ExplorerTableExportColumn>>(
    () => [
      ...columnDefs.map(
        (cd): ExplorerTableExportColumn => ({
          kind: 'data',
          title: cd.title,
          columnDef: cd,
        })
      ),
      ...enabledExtraColumns.map(
        (col): ExplorerTableExportColumn => ({
          kind: 'extra',
          title: col.name,
          column: col,
        })
      ),
    ],
    [columnDefs, enabledExtraColumns]
  )

  const handleExport = useCallback(
    async (format: ExplorerTableExportFormat) => {
      if (!apiURL) {
        errorNotification('Export is unavailable: Rolodex is not configured.')
        return
      }
      if (!exportColumns.length) {
        warningNotification('Add at least one column before exporting.')
        return
      }
      if (!orderedGroupIds.length) {
        warningNotification(
          'There are no rows to export with the current filters.'
        )
        return
      }

      setIsExporting(true)
      const dateSlug = dayjs().format('YYYYMMDD_HHmmss')
      const filename = `${slugifyTitle(panelTitle)}_${dateSlug}`
      infoNotification(
        `Exporting ${totalEntries.toLocaleString()} row${totalEntries === 1 ? '' : 's'}…`
      )
      try {
        const result = await runExplorerTableExport({
          axios,
          apiURL,
          groupByType,
          links,
          childLinks,
          orderedGroupIds,
          groupIdToBaseId,
          expandRecords,
          columns: exportColumns,
          schemaFields,
          format,
          filename,
          truncated,
        })
        if (result.truncatedByHistogram || result.truncatedByMaxRows) {
          warningNotification(
            `Export complete: ${result.rowsExported.toLocaleString()} of ${result.totalRows.toLocaleString()} rows. Results were truncated due to query limits.`
          )
        } else {
          successNotification(
            `Exported ${result.rowsExported.toLocaleString()} row${result.rowsExported === 1 ? '' : 's'}.`
          )
        }
      } catch (error) {
        if ((error as { name?: string }).name === 'AbortError') return
        console.error('Explorer table export failed', error)
        errorNotification(
          'Failed to export the table. See console for details.'
        )
      } finally {
        setIsExporting(false)
      }
    },
    [
      apiURL,
      axios,
      childLinks,
      errorNotification,
      expandRecords,
      exportColumns,
      groupByType,
      groupIdToBaseId,
      infoNotification,
      links,
      orderedGroupIds,
      panelTitle,
      schemaFields,
      successNotification,
      totalEntries,
      truncated,
      warningNotification,
    ]
  )

  const items = useMemo(
    () => [
      {
        key: 'xlsx',
        label: 'Excel (.xlsx)',
        onClick: () => handleExport('xlsx'),
      },
      {
        key: 'csv',
        label: 'CSV (.csv)',
        onClick: () => handleExport('csv'),
      },
    ],
    [handleExport]
  )

  const disabled = isExporting || isPlanLoading || !orderedGroupIds.length

  return (
    <Dropdown menu={{ items }} trigger={['click']} disabled={disabled}>
      <Button
        icon={<DownloadOutlined />}
        loading={isExporting}
        disabled={disabled}>
        Export
      </Button>
    </Dropdown>
  )
}
